import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  CircularProgress, 
  Typography, 
  IconButton,
  Stack,
  Fade,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import API_URL from '../config/api';

const MenuViewer = () => {
  const { restaurantId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showWelcome, setShowWelcome] = useState(true);
  const touchStartRef = useRef(null);
  const isZoomingRef = useRef(false);
  const initialTouchDistanceRef = useRef(null);

  // Increased minimum swipe distance threshold (in pixels)
  const minSwipeDistance = 80;

  // Minimum time for a swipe (to prevent accidental swipes)
  const minSwipeTime = 100;
  const maxSwipeTime = 1000;

  // Image navigation handlers
  const handleNextImage = useCallback(() => {
    if (!restaurant?.menu_images) return;
    setCurrentImageIndex(prev => 
      prev < restaurant.menu_images.length - 1 ? prev + 1 : 0
    );
  }, [restaurant?.menu_images]);

  const handlePrevImage = useCallback(() => {
    if (!restaurant?.menu_images) return;
    setCurrentImageIndex(prev => 
      prev > 0 ? prev - 1 : restaurant.menu_images.length - 1
    );
  }, [restaurant?.menu_images]);

  // Calculate distance between two touch points
  const getTouchDistance = useCallback((touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }, []);

  // Touch handlers
  const handleTouchStart = useCallback((e) => {
    if (e.touches.length === 2) {
      // Two finger touch - likely zooming
      isZoomingRef.current = true;
      initialTouchDistanceRef.current = getTouchDistance(e.touches);
      return;
    }

    // Single finger touch
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      time: Date.now()
    };
    isZoomingRef.current = false;
  }, [getTouchDistance]);

  const handleTouchMove = useCallback((e) => {
    // Prevent default only for horizontal swipes
    if (!isZoomingRef.current && touchStartRef.current) {
      const deltaX = Math.abs(e.touches[0].clientX - touchStartRef.current.x);
      const deltaY = Math.abs(e.touches[0].clientY - touchStartRef.current.y);
      if (deltaX > deltaY && deltaX > 10) {
        e.preventDefault();
      }
    }
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (isZoomingRef.current || !touchStartRef.current) {
      // Reset zooming state
      isZoomingRef.current = false;
      initialTouchDistanceRef.current = null;
      return;
    }

    const touchEndX = e.changedTouches[0].clientX;
    const touchEndTime = Date.now();
    const touchDuration = touchEndTime - touchStartRef.current.time;
    const distance = touchStartRef.current.x - touchEndX;

    // Only process swipe if:
    // 1. The swipe distance is greater than minimum threshold
    // 2. The swipe time is within acceptable range
    // 3. The vertical movement is less than horizontal movement
    const deltaY = Math.abs(e.changedTouches[0].clientY - touchStartRef.current.y);
    const deltaX = Math.abs(distance);

    if (deltaX > minSwipeDistance && 
        deltaX > deltaY * 1.5 &&
        touchDuration > minSwipeTime && 
        touchDuration < maxSwipeTime) {
      if (distance > 0) {
        handleNextImage();
      } else {
        handlePrevImage();
      }
    }

    touchStartRef.current = null;
  }, [handleNextImage, handlePrevImage, minSwipeDistance]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowWelcome(false);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const fetchRestaurant = async () => {
      try {
        const response = await fetch(`${API_URL}/api/restaurants/${restaurantId}`);
        if (!response.ok) {
          throw new Error('Restaurant not found');
        }
        const data = await response.json();
        setRestaurant(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurant();
  }, [restaurantId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        p={3}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Error
        </Typography>
        <Typography>
          {error}
        </Typography>
      </Box>
    );
  }

  if (!restaurant || !restaurant.menu_images || restaurant.menu_images.length === 0) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        p={3}
      >
        <Typography variant="h6" gutterBottom>
          No Menu Available
        </Typography>
        <Typography color="text.secondary">
          This restaurant hasn't uploaded their menu yet.
        </Typography>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        height: '100vh',
        width: '100vw',
        position: 'fixed',
        top: 0,
        left: 0,
        overflow: 'hidden',
        bgcolor: 'black',
        WebkitOverflowScrolling: 'touch', // Improve scrolling on iOS
      }}
    >
      {/* Welcome Overlay */}
      <Fade in={showWelcome} timeout={800}>
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255,255,255,0.98)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1200,
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              mb: 2,
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Welcome to
          </Typography>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              letterSpacing: '-0.5px',
              textAlign: 'center',
              px: 3
            }}
          >
            {restaurant.restaurant_name}
          </Typography>
        </Box>
      </Fade>

      {/* Background blur for image */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url(${restaurant.menu_images[currentImageIndex].image_url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'blur(20px)',
          opacity: 0.5,
          transform: 'scale(1.1)', // Prevent blur edges
        }}
      />

      {/* Main Content */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1,
        }}
      >
        {/* Restaurant name overlay */}
        <Fade in={!showWelcome} timeout={800}>
          <Box
            sx={{
              position: 'fixed',
              top: 20,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              maxWidth: '90%'
            }}
          >
            <Box
              sx={{
                position: 'relative',
                padding: '12px 30px',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(0,0,0,0.2)',
                  backdropFilter: 'blur(8px) saturate(180%)',
                  borderRadius: '16px',
                  border: '1px solid rgba(255,255,255,0.1)',
                  boxShadow: '0 4px 30px rgba(0, 0, 0, 0.1)',
                  zIndex: -1
                }
              }}
            >
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 500,
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,255,255,0.85))',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '0.5px',
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textShadow: '0 2px 15px rgba(0,0,0,0.15)',
                  fontFamily: '"Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, Arial, sans-serif',
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(135deg, rgba(255,255,255,0.2), rgba(255,255,255,0.1))',
                    filter: 'blur(4px)',
                    opacity: 0.7,
                    pointerEvents: 'none'
                  }
                }}
              >
                {restaurant.restaurant_name}
              </Typography>
            </Box>
          </Box>
        </Fade>

        {/* Image Container with touch handlers */}
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <IconButton 
            onClick={handlePrevImage}
            sx={{
              position: 'fixed',
              left: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              '&:hover': { 
                bgcolor: 'rgba(255,255,255,0.3)',
              },
              transition: 'all 0.2s ease',
              color: 'white'
            }}
          >
            <ChevronLeftIcon />
          </IconButton>

          <img
            src={restaurant.menu_images[currentImageIndex].image_url}
            alt={`Menu page ${currentImageIndex + 1}`}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              transition: 'opacity 0.2s ease',
              touchAction: 'pinch-zoom',
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none',
              WebkitTapHighlightColor: 'transparent',
              transform: 'translateZ(0)', // Force GPU acceleration
            }}
          />

          <IconButton 
            onClick={handleNextImage}
            sx={{
              position: 'fixed',
              right: 16,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              bgcolor: 'rgba(255,255,255,0.2)',
              backdropFilter: 'blur(10px)',
              '&:hover': { 
                bgcolor: 'rgba(255,255,255,0.3)',
              },
              transition: 'all 0.2s ease',
              color: 'white'
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>

        {/* Page indicators */}
        {restaurant.menu_images.length > 1 && (
          <Stack 
            direction="row" 
            spacing={1.5} 
            justifyContent="center"
            sx={{ 
              position: 'fixed',
              bottom: 20,
              left: 0,
              right: 0,
              zIndex: 2,
              px: 2
            }}
          >
            {restaurant.menu_images.map((_, index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: index === currentImageIndex ? 'white' : 'rgba(255,255,255,0.5)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.2)',
                    bgcolor: index === currentImageIndex ? 'white' : 'rgba(255,255,255,0.7)'
                  }
                }}
                onClick={() => setCurrentImageIndex(index)}
              />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default MenuViewer;
